<script setup>
import {ref, computed, onMounted, getCurrentInstance} from 'vue'
import PrintReport from './PrintReport'
import moment from 'moment'
import * as CSV from 'csv-string'
import ReportMenu from './ReportMenu'
import DateRangeFilter from "./filters/DateRangeFilter"
import PrimaryCategoryFilter from "../../bespoke/printgifts/reports/PrimaryCategoryFilter.vue"
import Button from "../../common/Button.vue"

// Context & Proxy References
const instance = getCurrentInstance()
const store = instance.proxy.$store
const router = instance.proxy.$router
const route = instance.proxy.$route

// Template Elements DOM Refs
const formRef = ref(null)
const toPrint = ref(null)

// Local Reactive State
const error = ref('')
const sortBy = ref('')
const sortDir = ref('asc')

// Computed Mappings
const reportsModule = computed(() => store.state.printGiftsReports)
const formData = computed(() => reportsModule.value.formData)
const isLoading = computed(() => reportsModule.value.isLoading)
const rows = computed(() => reportsModule.value.rows)
const startDate = computed(() => reportsModule.value.formData.startDate)
const endDate = computed(() => reportsModule.value.formData.endDate)

const reportLabel = computed(() => {
    if (!formData.value.reportName || !reportsModule.value.reports) return ''
    const activeReport = reportsModule.value.reports.find(o => o.name === formData.value.reportName)
    return activeReport ? activeReport.label : ''
})

const sortedRows = computed(() => {
    if (!rows.value) return []
    // Create a shallow copy to prevent mutations of structural store arrays during sorting
    return [...rows.value].sort((a, b) => {
        if (sortBy.value === 'Credit amount') {
            if (sortDir.value === 'desc') {
                return b[sortBy.value] - a[sortBy.value]
            } else {
                return a[sortBy.value] - b[sortBy.value]
            }
        } else {
            if (sortDir.value === 'desc') {
                return b[sortBy.value] > a[sortBy.value] ? 1 : -1
            } else {
                return a[sortBy.value] > b[sortBy.value] ? 1 : -1
            }
        }
    })
})

// Lifecycle Hooks
onMounted(() => {
    store.dispatch('printGiftsReports/init', {
        query: route.query
    })
})

// Methods / Local Utility Functions
function formatNumber(value) {
    return new Intl.NumberFormat().format(value)
}

function setSortBy(heading) {
    sortDir.value = sortBy.value === heading
        ? (sortDir.value === 'asc' ? 'desc' : 'asc')
        : 'asc'

    sortBy.value = heading
}

function submitForm() {
    const form = formRef.value
    error.value = ''

    if (form && !form.checkValidity()) {
        Array.from(form.elements).forEach((element) => {
            if (element.validity.valid === false && error.value === '') {
                error.value += element.validationMessage
            }
        })
        return
    }

    router.push({query: formData.value})
    store.dispatch('printGiftsReports/loadRows')
}

function getDate(timestamp) {
    return moment(timestamp).format('DD-MM-Y')
}

function openPrintModal() {
    store.dispatch('modals/show', {
        componentName: 'PrintReportModal',
        obj: {html: toPrint.value.$el.innerHTML}
    })
}

function downloadCSVData() {
    if (!rows.value || !rows.value.length) return

    // Headings
    const headings = Object.keys(rows.value[0])
    let csv = CSV.stringify(headings)

    // Body
    rows.value.forEach((row) => {
        const values = Object.values(row)
        csv += CSV.stringify(values)
    })

    const anchor = document.createElement('a')
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    anchor.target = '_blank'
    anchor.download = `${reportLabel.value} ${getDate(new Date())}.csv`
    anchor.click()
}
</script>

<template>
    <div>
        <div>
            <nav class="navbar navbar-default">
                <div class="container-fluid">
                    <form ref="formRef" class="float-start d-flex align-items-center gap-1">
                        <ReportMenu :reports-module="reportsModule"/>

                        <template v-if="formData.reportName === 'relatedProducts'">
                            <PrimaryCategoryFilter/>
                            <DateRangeFilter :reports-module="reportsModule"/>
                        </template>

                        <button v-if="formData.reportName !== ''" class="ms-2 btn btn-primary btn-sm"
                                @click.prevent="submitForm">Go
                        </button>

                        <span v-if="rows && rows.length" class="ms-2 text-nowrap">
                            {{ formatNumber(rows.length) }} records found
                        </span>

                        <span v-if="isLoading" class="ms-2 ajax-spinner">
                            <span class="ajax-spinner-icon ajax-spinner-icon-sm"/>
                        </span>
                    </form>

                    <div v-if="rows && rows.length" class="float-end">
                        <Button @click.native="openPrintModal">
                            <i class="bi-printer"/>
                            <span class="visually-hidden">Print report</span>
                        </Button>

                        <Button @click.native.prevent="downloadCSVData">
                            <i class="bi-download"/>
                            <span class="visually-hidden">Download CSV</span>
                        </Button>
                    </div>

                </div>
            </nav>

            <p v-if="rows && rows.length === 0" class="mx-2 alert alert-warning">
                No records match your search.
            </p>

            <p v-if="error" class="mx-2 alert alert-warning" v-html="error"></p>

            <div v-if="rows && rows.length" class="padding-sm table-wrapper">
                <table class="table table-condensed table-striped">
                    <thead>
                    <tr>
                        <td v-for="(value, heading) in rows[0]" :key="'head-' + heading" @click="setSortBy(heading)">
                            <span v-if="heading === sortBy"
                                  :class="{ 'bi-chevron-up': sortDir === 'asc', 'bi-chevron-down': sortDir === 'desc' }"/>
                            {{ heading }}
                        </td>
                    </tr>
                    </thead>

                    <tbody>
                    <tr v-for="(row, rowIndex) in sortedRows" :key="'row-' + rowIndex">
                        <td v-for="(value, heading) in row" :key="'cell-' + heading" v-html="value"></td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <PrintReport v-if="rows" ref="toPrint" :heading="reportLabel" :rows="sortedRows" :start-date="startDate"
                     style="display: none"/>
    </div>
</template>

<style scoped>
.ajax-spinner {
    vertical-align: bottom;
}
</style>